import { ref, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useQueryClient } from '@tanstack/vue-query';
import { useRouteQueryParam } from 'src/composables/useRouteQueryParam';
import {
  usePostFetchAllContracts,
  useGetFetchContract,
  getGetFetchContractQueryKey,
  usePostCreateContract,
  usePatchPatchContract,
  usePostAddActionItemToContract,
  usePostCreateActionItem,
  usePutUpdateActionItem,
  usePostFetchAllRecipes,
  usePostFetchAllOrganisations,
  usePostFetchAllOrganisationBranches,
  usePostGetAllOrganisationContactPersons,
  getFetchUnitsByOrganisation,
  usePostUpdateContractStatus,
  usePostUpdateNoteToContract,
} from 'src/api/queries/order-portal-bff';
import type { FetchContractResponse } from 'src/api/models/fetchContractResponse';
import type { CreateContractDTO } from 'src/api/models/createContractDTO';
import type { PatchContractDTO } from 'src/api/models/patchContractDTO';
import type { OrganisationFilterDTO } from 'src/api/models/organisationFilterDTO';
import type { FetchUnitResponseLite } from 'src/api/models/fetchUnitResponseLite';
import type { AbstractActionFiltersDTO } from 'src/api/models/abstractActionFiltersDTO';
import type { CreateActionItemDTO } from 'src/api/models/createActionItemDTO';
import type { UpdateActionItemDTO } from 'src/api/models/updateActionItemDTO';
import type { NestedActionItemResponse } from 'src/api/models/nestedActionItemResponse';
import type { NestedActionItemsDetailResponse } from 'src/api/models/nestedActionItemsDetailResponse';
import { CreateContractDTOType } from 'src/api/models/createContractDTOType';
import type { PatchContractDTOType } from 'src/api/models/patchContractDTOType';
import { FetchContractResponseStatus } from 'src/api/models/fetchContractResponseStatus';
import { UpdateContractStatusDTOStatus } from 'src/api/models/updateContractStatusDTOStatus';
import { useCheckUnitActiveContract } from 'src/api/stubs/contract-stubs';
import { CreateContractDTOPeriod } from 'src/api/models';
import { PatchContractDTOPeriod } from 'src/api/models/patchContractDTOPeriod';

export type RecipePricingMode = 'fixed' | 'hourly';

export const SUPPORTED_RECIPE_TYPES = ['service', 'inspection'] as const;
export type SupportedRecipeType = typeof SUPPORTED_RECIPE_TYPES[number];

export function isSupportedRecipeType(type: string | null | undefined): type is SupportedRecipeType {
  return !!type && (SUPPORTED_RECIPE_TYPES as readonly string[]).includes(type);
}

export type RecipeEntry = {
  recipe_id: string | null;
  recipe_name: string | null;
  recipe_type: string | null;
  pricing_mode: RecipePricingMode;
  tariff: number | null;
  interval: number | null;
  hourly_running_rate: number | null;
  running_hours_per_year: number | null;
  action_item_id: string | null;
};

export type ContractPeriodValue = number | 'indefinite' | null;

// Backend will be extended to accept these fields on patch. Loosen the payload here so
// the UI can submit them today; once the OpenAPI spec is regenerated by Orval the cast
// inside updateContract becomes a no-op.
export type ContractPatchData = Partial<PatchContractDTO> & {
  start_date?: string | null;
  reference_number?: string | null;
  contract_holder_id?: string | null;
  operator_id?: string | null;
  unit_id?: string | null;
  contact_id?: string | null;
  location_id?: string | null;
};

export type ContractFormData = Pick<
  CreateContractDTO,
  'contract_holder_id' | 'operator_id' | 'unit_id' | 'reference_number' | 'start_date' | 'annual_price_adjustment' | 'type'
> & {
  // TODO: backend will add this field
  contact_id: string | null;
  // TODO: backend will add this field
  location_id: string | null;
  period: ContractPeriodValue;
  running_hour_bat_start: number | null;
  // TODO: backend will add this field
  recipes: RecipeEntry[];
  note: string;
};

export function emptyRecipeEntry(): RecipeEntry {
  return {
    recipe_id: null,
    recipe_name: null,
    recipe_type: null,
    pricing_mode: 'fixed',
    tariff: null,
    interval: null,
    hourly_running_rate: null,
    running_hours_per_year: null,
    action_item_id: null,
  };
}
export const CONTRACT_PERIODS: { label: string; value: number | 'indefinite' }[] = [
  { label: '24 months', value: 24 },
  { label: '36 months', value: 36 },
  { label: '48 months', value: 48 },
  { label: '60 months', value: 60 },
  { label: '72 months', value: 72 },
  { label: '84 months', value: 84 },
  { label: 'Indefinite', value: 'indefinite' },
];

export const CONTRACT_TYPES: { label: string; value: CreateContractDTOType }[] = [
  { label: 'Care', value: CreateContractDTOType.care },
  { label: 'Full care', value: CreateContractDTOType.full_care },
];

export function validateContractForm(data: ContractFormData): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.contract_holder_id) errors.customer = 'Customer is required';
  if (!data.operator_id) errors.supplier = 'Supplier is required';
  if (!data.location_id) errors.location = 'Location is required';
  if (!data.unit_id) errors.unit = 'Unit is required';


  data.recipes.forEach((entry, index) => {
    if (!entry.recipe_id) return;
    if (!isSupportedRecipeType(entry.recipe_type)) {
      errors[`recipe_${index}_type`] = 'Recipe-type wordt niet ondersteund';
      return;
    }
    if (entry.interval === null) {
      errors[`recipe_${index}_interval`] = 'Required when a recipe is selected';
    }
    const isHourly = entry.recipe_type === 'service' && entry.pricing_mode === 'hourly';
    if (isHourly) {
      if (entry.hourly_running_rate === null) {
        errors[`recipe_${index}_hourly_rate`] = 'Required for hourly pricing';
      }
      if (entry.running_hours_per_year === null) {
        errors[`recipe_${index}_hours_per_year`] = 'Required for hourly pricing';
      }
    } else if (entry.tariff === null) {
      errors[`recipe_${index}_tariff`] = 'Required when a recipe is selected';
    }
  });

  return errors;
}

function fetchPeriodToFormValue(period: string | null | undefined): ContractPeriodValue {
  if (!period) return null;
  if (period === 'indefinite') return 'indefinite';
  const match = String(period).match(/^(\d+)/);
  return match ? Number(match[1]) : null;
}

function actionItemToRecipeEntry(
  item: NestedActionItemResponse,
  detail: NestedActionItemsDetailResponse[] | undefined,
): RecipeEntry | null {
  if (!item.id) return null;
  const recipeId = detail?.find((d) => d.id === item.id)?.recipe?.id ?? null;
  if (!recipeId) return null;
  const isHourly = item.hourly_running_rate !== null && item.hourly_running_rate !== undefined;
  return {
    recipe_id: recipeId,
    recipe_name: item.recipe_name ?? null,
    recipe_type: item.recipe_type ?? null,
    pricing_mode: isHourly ? 'hourly' : 'fixed',
    tariff: item.price ?? null,
    interval: item.interval_in_months ?? null,
    hourly_running_rate: item.hourly_running_rate ?? null,
    running_hours_per_year: item.running_hours_per_year ?? null,
    action_item_id: item.id,
  };
}

export function contractToFormData(contract: FetchContractResponse): ContractFormData {
  const recipes = (contract.action_items ?? [])
    .map((item) => actionItemToRecipeEntry(item, contract.action_items_detail))
    .filter((r): r is RecipeEntry => r !== null);
  const firstItem = contract.action_items?.[0];

  console.log(contract)
  return {
    contract_holder_id: contract.contract_holder?.id ?? '',
    operator_id: contract.operator?.id ?? '',
    contact_id: contract.contract_holder_contact_person?.id ?? null,
    location_id: contract.operator_branch?.id ?? null,
    unit_id: contract.unit?.id ?? '',
    reference_number: contract.reference_number ?? '',
    start_date: contract.start_date ? contract.start_date.slice(0, 10) : '',
    period: fetchPeriodToFormValue(contract.period),
    type: (contract.type as unknown as CreateContractDTOType) ?? CreateContractDTOType.care,
    running_hour_bat_start: firstItem?.running_hour_bat_start ?? null,
    annual_price_adjustment: contract.annual_price_adjustment ?? false,
    recipes: recipes.length > 0 ? recipes : [emptyRecipeEntry()],
    note: contract.note ?? '',
  };
}

export function emptyContractForm(): ContractFormData {
  return {
    contract_holder_id: '',
    operator_id: '',
    contact_id: null,
    location_id: null,
    unit_id: '',
    reference_number: '',
    start_date: '',
    period: null,
    type: CreateContractDTOType.care,
    running_hour_bat_start: null,
    annual_price_adjustment: true,
    recipes: [emptyRecipeEntry()],
    note: '',
  };
}

let globalLoadRequestId = 0;

export function useContractManagement() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const contracts = ref<FetchContractResponse[]>([]);
  const loading = ref(false);
  const page = ref(1);
  const totalPages = ref(1);
  const totalRecords = ref(0);

  const searchQuery = ref('');
  const statusFilter = ref<FetchContractResponseStatus | null>(null);

  const selectedContractId = useRouteQueryParam('contractId');
  const operationInProgress = ref(false);

  const { mutateAsync: fetchAllContracts } = usePostFetchAllContracts();
  const { mutateAsync: createContractMutation } = usePostCreateContract();
  const { mutateAsync: patchContractMutation } = usePatchPatchContract();
  const { mutateAsync: addActionItemsMutation } = usePostAddActionItemToContract();
  const { mutateAsync: createActionItemMutation } = usePostCreateActionItem();
  const { mutateAsync: updateActionItemMutation } = usePutUpdateActionItem();
  const { mutateAsync: fetchAllRecipes } = usePostFetchAllRecipes();
  const { mutateAsync: fetchOrganisations } = usePostFetchAllOrganisations();
  const { mutateAsync: fetchBranches } = usePostFetchAllOrganisationBranches();
  const { mutateAsync: fetchContactPersons } = usePostGetAllOrganisationContactPersons();
  const { mutateAsync: checkActiveContract } = useCheckUnitActiveContract();
  const { mutateAsync: updateNote } = usePostUpdateNoteToContract();
  const { mutateAsync: updateContractStatus } = usePostUpdateContractStatus();

  const selectedContractQuery = useGetFetchContract(
    computed(() => selectedContractId.value ?? ''),
    {
      query: {
        enabled: computed(() => !!selectedContractId.value),
        placeholderData: (previousData: FetchContractResponse | undefined) => previousData,
      },
    },
  );

  const selectedContract = computed<FetchContractResponse | null>(
    () => selectedContractQuery.data?.value ?? null,
  );
  const selectedContractLoading = computed(() => selectedContractQuery.isFetching?.value ?? false);

  async function loadContracts(targetPage = 1) {
    const thisRequest = ++globalLoadRequestId;
    loading.value = true;
    try {
      const response = await fetchAllContracts({
        data: {},
        params: { page: targetPage, limit: 15 },
      });
      if (thisRequest !== globalLoadRequestId) return;
      contracts.value = response.data ?? [];
      page.value = response.page ?? 1;
      const raw = response as Record<string, unknown>;
      totalPages.value = typeof raw.total_pages === 'number' ? raw.total_pages : 1;
      totalRecords.value = typeof raw.total_records === 'number'
        ? raw.total_records
        : (totalPages.value * 15);
    } catch {
      if (thisRequest !== globalLoadRequestId) return;
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan contracten niet laden', life: 3000 });
    } finally {
      if (thisRequest === globalLoadRequestId) loading.value = false;
    }
  }

  function selectContract(id: string | null) {
    selectedContractId.value = id;
  }

  async function refreshSelectedContract() {
    if (selectedContractId.value) {
      queryClient.invalidateQueries({ queryKey: getGetFetchContractQueryKey(selectedContractId.value) });
    }
  }

  async function createContract(
    data: ContractFormData,
    options?: { silent?: boolean },
  ): Promise<string | null> {
    operationInProgress.value = true;
    try {
      const payload: CreateContractDTO = {
        contract_holder_id: data.contract_holder_id,
        operator_id: data.operator_id,
        unit_id: data.unit_id,
        contract_holder_contact_person_id: data.contact_id || undefined,
        operator_branch_id: data.location_id || undefined,
        reference_number: data.reference_number || undefined,
        start_date: data.start_date || null,
        type: data.type,
        period: data.period === null || data.period === 'indefinite'
          ? CreateContractDTOPeriod.indefinite
          : (String(data.period) as CreateContractDTOPeriod),
        annual_price_adjustment: data.annual_price_adjustment ?? false,
      };
      const newId = await createContractMutation({ data: payload });
      if (!options?.silent) {
        toast.add({ severity: 'success', summary: 'Succes', detail: 'Contract aangemaakt', life: 3000 });
        await loadContracts(page.value);
      }
      if (newId) {
        selectedContractId.value = newId as unknown as string;
      }
      return (newId as unknown as string) ?? null;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan contract niet aanmaken', life: 3000 });
      throw new Error('Create failed');
    } finally {
      operationInProgress.value = false;
    }
  }

  function recipeEntryToActionItemPayload(
    entry: RecipeEntry,
    runningHourBatStart: number | null,
  ): CreateActionItemDTO {
    const isHourly = entry.recipe_type === 'service' && entry.pricing_mode === 'hourly';
    return {
      recipe_id: entry.recipe_id as string,
      quantity: 1,
      interval_in_months: entry.interval,
      price: isHourly ? (null as unknown as number) : (entry.tariff as number),
      ...(isHourly
        ? {
            hourly_running_rate: entry.hourly_running_rate,
            running_hours_per_year: entry.running_hours_per_year,
          }
        : {}),
      ...(runningHourBatStart !== null ? { running_hour_bat_start: runningHourBatStart } : {}),
    };
  }

  async function addContractActionItems(
    id: string,
    recipes: RecipeEntry[],
    runningHourBatStart: number | null,
    options?: { silent?: boolean },
  ): Promise<boolean> {
    const submittable = recipes.filter(
      (r) => !r.action_item_id && !!r.recipe_id && isSupportedRecipeType(r.recipe_type),
    );
    if (submittable.length === 0) return true;
    operationInProgress.value = true;
    try {
      const actionItemIds = await Promise.all(
        submittable.map((r) =>
          createActionItemMutation({ data: recipeEntryToActionItemPayload(r, runningHourBatStart) }),
        ),
      );
      await addActionItemsMutation({ id, data: { action_items: actionItemIds } });
      await refreshSelectedContract();
      if (!options?.silent) {
        toast.add({ severity: 'success', summary: 'Succes', detail: 'Actiepunten bijgewerkt', life: 3000 });
        await loadContracts(page.value);
      }
      return true;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan actiepunten niet bijwerken', life: 3000 });
      return false;
    } finally {
      operationInProgress.value = false;
    }
  }

  async function createContractWithActionItems(data: ContractFormData): Promise<string | null> {
    let newId: string | null = null;
    try {
      newId = await createContract(data, { silent: true });
    } catch {
      return null;
    }
    if (!newId) return null;

    const success = await addContractActionItems(newId, data.recipes, data.running_hour_bat_start, { silent: true });
    await loadContracts(page.value);
    if (success) {
      toast.add({ severity: 'success', summary: 'Succes', detail: 'Contract aangemaakt', life: 3000 });
    }
    return newId;
  }

  async function resetStatusIfOfferSent(id: string): Promise<void> {
    const current = selectedContract.value;
    if (current?.id !== id) return;
    if (current.status !== FetchContractResponseStatus.offer_sent) return;
    await updateContractStatus({ id, data: { status: UpdateContractStatusDTOStatus.new } });
  }

  async function updateContract(id: string, data: ContractPatchData): Promise<boolean> {
    operationInProgress.value = true;
    try {
      await patchContractMutation({ id, data: data as PatchContractDTO });
      await resetStatusIfOfferSent(id);
      toast.add({ severity: 'success', summary: 'Succes', detail: 'Contract bijgewerkt', life: 3000 });
      await refreshSelectedContract();
      await loadContracts(page.value);
      return true;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan contract niet bijwerken', life: 3000 });
      return false;
    } finally {
      operationInProgress.value = false;
    }
  }

  async function updateContractNote(id: string, note: string): Promise<boolean> {
    operationInProgress.value = true;
    try {
      await updateNote({ id, data: { note } });
      toast.add({ severity: 'success', summary: 'Succes', detail: 'Notitie opgeslagen', life: 3000 });
      await refreshSelectedContract();
      return true;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan notitie niet opslaan', life: 3000 });
      return false;
    } finally {
      operationInProgress.value = false;
    }
  }

  async function updateActionItem(actionItemId: string, data: UpdateActionItemDTO): Promise<boolean> {
    operationInProgress.value = true;
    try {
      await updateActionItemMutation({ id: actionItemId, data });
      return true;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan actiepunt niet bijwerken', life: 3000 });
      return false;
    } finally {
      operationInProgress.value = false;
    }
  }

  async function saveContractEdits(id: string, data: ContractFormData): Promise<boolean> {
    const contractPatch: ContractPatchData = {
      contract_holder_id: data.contract_holder_id || null,
      operator_id: data.operator_id || null,
      unit_id: data.unit_id || null,
      contract_holder_contact_person_id: data.contact_id ?? null,
      operator_branch_id: data.location_id ?? null,
      start_date: data.start_date || null,
      period: data.period === null || data.period === 'indefinite'
        ? PatchContractDTOPeriod.indefinite
        : (String(data.period) as PatchContractDTOPeriod),
      type: data.type as unknown as PatchContractDTOType,
      reference_number: data.reference_number || null,
      annual_price_adjustment: data.annual_price_adjustment ?? false,
    };

    const actionItemUpdates = data.recipes
      .filter(
        (r): r is RecipeEntry & { action_item_id: string } =>
          !!r.action_item_id && !!r.recipe_id && isSupportedRecipeType(r.recipe_type),
      )
      .map((r) => ({
        id: r.action_item_id,
        data: recipeEntryToActionItemPayload(r, data.running_hour_bat_start) as UpdateActionItemDTO,
      }));

    const newActionItems = data.recipes
      .filter((r) => !r.action_item_id && !!r.recipe_id && isSupportedRecipeType(r.recipe_type))
      .map((r) => recipeEntryToActionItemPayload(r, data.running_hour_bat_start));

    return updateContractWithActionItems(id, contractPatch, actionItemUpdates, newActionItems);
  }

  async function updateContractWithActionItems(
    id: string,
    contractData: ContractPatchData,
    actionItemUpdates: { id: string; data: UpdateActionItemDTO }[],
    newActionItems: CreateActionItemDTO[] = [],
  ): Promise<boolean> {
    operationInProgress.value = true;
    try {
      if (Object.keys(contractData).length > 0) {
        await patchContractMutation({ id, data: contractData as PatchContractDTO });
      }
      if (actionItemUpdates.length > 0) {
        await Promise.all(
          actionItemUpdates.map((u) => updateActionItemMutation({ id: u.id, data: u.data })),
        );
      }
      if (newActionItems.length > 0) {
        const newActionItemIds = await Promise.all(
          newActionItems.map((payload) => createActionItemMutation({ data: payload })),
        );
        await addActionItemsMutation({ id, data: { action_items: newActionItemIds } });
      }
      await resetStatusIfOfferSent(id);
      toast.add({ severity: 'success', summary: 'Succes', detail: 'Contract bijgewerkt', life: 3000 });
      await refreshSelectedContract();
      await loadContracts(page.value);
      return true;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan contract niet bijwerken', life: 3000 });
      return false;
    } finally {
      operationInProgress.value = false;
    }
  }

  async function activateContract(id: string): Promise<boolean> {
    operationInProgress.value = true;
    try {
      await updateContractStatus({ id, data: { status: UpdateContractStatusDTOStatus.active } });
      toast.add({ severity: 'success', summary: 'Succes', detail: 'Contract geactiveerd', life: 3000 });
      await refreshSelectedContract();
      await loadContracts(page.value);
      return true;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan contract niet activeren', life: 3000 });
      return false;
    } finally {
      operationInProgress.value = false;
    }
  }

  async function offerContract(id: string): Promise<boolean> {
    operationInProgress.value = true;
    try {
      await updateContractStatus({ id, data: { status: UpdateContractStatusDTOStatus.offer_sent } });
      toast.add({ severity: 'success', summary: 'Succes', detail: 'Contract aangeboden', life: 3000 });
      await refreshSelectedContract();
      await loadContracts(page.value);
      return true;
    } catch {
      toast.add({ severity: 'error', summary: 'Fout', detail: 'Kan contract niet aanbieden', life: 3000 });
      return false;
    } finally {
      operationInProgress.value = false;
    }
  }

  async function checkUnitHasActiveContract(unitId: string): Promise<boolean> {
    try {
      return await checkActiveContract({ unitId });
    } catch {
      return false;
    }
  }

  async function searchOrganisations(query: string, pageNum: number) {
    const filters: OrganisationFilterDTO = {};
    if (query.trim()) filters.name = query.trim();
    const response = await fetchOrganisations({
      data: filters,
      params: { page: pageNum, limit: 20 },
    });
    const raw = response as Record<string, unknown>;
    return {
      data: response.data ?? [],
      page: typeof raw.page === 'number' ? raw.page : 1,
      total_pages: typeof raw.total_pages === 'number' ? raw.total_pages : 1,
    };
  }

  const unitsByOrgCache = new Map<string, FetchUnitResponseLite[]>();

  function unitMatchesQuery(unit: FetchUnitResponseLite, q: string): boolean {
    return (
      (unit.name?.toLowerCase().includes(q) ?? false) ||
      (unit.manufacture_name?.toLowerCase().includes(q) ?? false) ||
      (unit.serial_number?.toLowerCase().includes(q) ?? false) ||
      (unit.license_plate_number?.toLowerCase().includes(q) ?? false) ||
      (unit.fleet_number != null && String(unit.fleet_number).includes(q))
    );
  }

  async function searchUnits(organisationId: string, query: string) {
    let units = unitsByOrgCache.get(organisationId);
    if (!units) {
      const response = await getFetchUnitsByOrganisation(organisationId);
      units = response ?? [];
      unitsByOrgCache.set(organisationId, units);
    }
    const trimmed = query.trim().toLowerCase();
    const data = trimmed ? units.filter((u) => unitMatchesQuery(u, trimmed)) : units;
    return { data, page: 1, total_pages: 1 };
  }

  async function searchBranches(organisationId: string | null, _query: string, _pageNum: number) {
    if (!organisationId) return { data: [], page: 1, total_pages: 1 };
    const response = await fetchBranches({ id: organisationId });
    return {
      data: response ?? [],
      page: 1,
      total_pages: 1,
    };
  }

  async function searchContactPersons(organisationId: string, _query: string, _pageNum: number) {
    const response = await fetchContactPersons({ id: organisationId });
    const items = Array.isArray(response) ? response : [];
    return {
      data: items,
      page: 1,
      total_pages: 1,
    };
  }

  // TODO: Add contract_eligible filter when backend supports it
  async function searchRecipes(query: string, pageNum: number) {
    const filters: AbstractActionFiltersDTO = {};
    if (query.trim()) filters.name = query.trim();
    const response = await fetchAllRecipes({
      data: filters,
      params: { page: pageNum, limit: 20 },
    });
    const raw = response as Record<string, unknown>;
    return {
      data: response.data ?? [],
      page: typeof raw.page === 'number' ? raw.page : 1,
      total_pages: typeof raw.total_pages === 'number' ? raw.total_pages : 1,
    };
  }

  return {
    contracts,
    loading,
    page,
    totalPages,
    totalRecords,
    searchQuery,
    statusFilter,
    selectedContractId,
    selectedContract,
    selectedContractLoading,
    operationInProgress,
    loadContracts,
    selectContract,
    createContract,
    addContractActionItems,
    createContractWithActionItems,
    updateContract,
    updateContractNote,
    updateActionItem,
    updateContractWithActionItems,
    saveContractEdits,
    refreshSelectedContract,
    activateContract,
    offerContract,
    checkUnitHasActiveContract,
    searchOrganisations,
    searchUnits,
    searchBranches,
    searchContactPersons,
    searchRecipes,
  };
}
